import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useAltHotkeys } from '../../hooks/useAltHotkeys';
import { useModalDrag } from '../../hooks/useModalDrag';
import ModalHeader from '../../components/ui/ModalHeader';
import { ArrowPathIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import saverulesDashboardService, {
  DrilldownParams,
  DrilldownRow,
  DrilldownSection,
  ReconcilerDrillRow,
  ReconcilerLine,
  RollupRow,
  TrendPoint,
} from '../../services/saverulesDashboard.service';

export interface DrilldownTarget {
  title: string;
  section: DrilldownSection;
  bucket?: string;
  area?: string;
  optype?: string;
}

interface Props {
  target: DrilldownTarget | null;
  days: number;
  onClose: () => void;
  onOpenPro: (pronumber: string | number) => void;
}

const PAGE = 200;
const isRecon = (s: DrilldownSection) => s === 'reconciler';

/** Tiny inline sparkline of per-day diff counts. */
const Sparkline: React.FC<{ points: TrendPoint[] }> = ({ points }) => {
  if (points.length < 2) return null;
  const w = 120, h = 28, max = Math.max(1, ...points.map((p) => p.n));
  const step = w / (points.length - 1);
  const path = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${(i * step).toFixed(1)},${(h - (p.n / max) * h).toFixed(1)}`)
    .join(' ');
  const last = points[points.length - 1];
  return (
    <span className="flex items-center gap-2" title={`per-day diff count (max ${max})`}>
      <svg width={w} height={h} className="overflow-visible">
        <path d={path} fill="none" stroke="currentColor" strokeWidth={1.5}
          className="text-sky-500" />
      </svg>
      <span className="text-xs text-slate-400 tabular-nums">
        {last.date.slice(5)}: {last.n}
      </span>
    </span>
  );
};

const fmtTime = (s: string | null) => (s ? s.replace('T', ' ').slice(0, 19) : '');

const ProLink: React.FC<{ pro: string | number | null; onOpenPro: Props['onOpenPro'] }> = ({
  pro,
  onOpenPro,
}) =>
  pro == null || pro === '' ? (
    <span className="text-slate-400">—</span>
  ) : (
    <button
      className="text-sky-600 dark:text-sky-400 hover:underline"
      onClick={() => onOpenPro(pro)}
    >
      {pro}
    </button>
  );

const SaverulesDrilldownModal: React.FC<Props> = ({ target, days, onClose, onOpenPro }) => {
  const [rows, setRows] = useState<(DrilldownRow | ReconcilerDrillRow)[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<{ user?: string; rule?: string; field?: string }>({});
  const [tab, setTab] = useState<'rows' | 'rule' | 'user'>('rows');
  const [rollup, setRollup] = useState<RollupRow[]>([]);
  const [trend, setTrend] = useState<TrendPoint[]>([]);
  const [expandedPro, setExpandedPro] = useState<string | number | null>(null);
  const [proLines, setProLines] = useState<Record<string, ReconcilerLine[]>>({});
  const [saveDrill, setSaveDrill] = useState<{ requestId: string; pronumber: string | number } | null>(null);
  const qDebounce = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const recon = !!target && isRecon(target.section);

  // Reset transient state when the target changes.
  useEffect(() => {
    setQ('');
    setFilter({});
    setTab('rows');
    setSaveDrill(null);
    setExpandedPro(null);
  }, [target]);

  const params = useMemo<DrilldownParams | null>(() => {
    if (!target) return null;
    if (saveDrill) {
      return { section: 'save', days, request_id: saveDrill.requestId, limit: PAGE };
    }
    return {
      section: target.section,
      days,
      bucket: target.bucket,
      area: target.area,
      optype: target.optype,
      q: q || undefined,
      user: filter.user,
      rule: filter.rule,
      field: filter.field,
      limit: PAGE,
    };
  }, [target, days, q, filter, saveDrill]);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      if (!params) return;
      setLoading(true);
      try {
        const res = await saverulesDashboardService.getDrilldown(params, signal);
        setRows(res.rows);
        setTotal(res.total);
        setError(null);
      } catch (e: any) {
        if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED') return;
        setError(e?.response?.data?.detail ?? e?.message ?? 'Failed to load drill-down');
      } finally {
        setLoading(false);
      }
    },
    [params],
  );

  // Debounced reload on any param change.
  useEffect(() => {
    if (!target) return;
    const controller = new AbortController();
    clearTimeout(qDebounce.current);
    qDebounce.current = setTimeout(() => load(controller.signal), 250);
    return () => {
      clearTimeout(qDebounce.current);
      controller.abort();
    };
  }, [target, load]);

  // Trend sparkline (once per target).
  useEffect(() => {
    if (!target) return;
    const c = new AbortController();
    saverulesDashboardService
      .getTrend(target.section, Math.max(days, 14), target.bucket ?? '', c.signal)
      .then(setTrend)
      .catch(() => setTrend([]));
    return () => c.abort();
  }, [target, days]);

  // Rollup when a rollup tab is active.
  useEffect(() => {
    if (!target || tab === 'rows') return;
    const c = new AbortController();
    saverulesDashboardService
      .getRollup(tab, target.section, days, target.bucket ?? '', c.signal)
      .then(setRollup)
      .catch(() => setRollup([]));
    return () => c.abort();
  }, [target, tab, days]);

  const toggleProLines = useCallback(
    async (pro: string | number) => {
      if (expandedPro === pro) {
        setExpandedPro(null);
        return;
      }
      setExpandedPro(pro);
      if (!proLines[String(pro)]) {
        try {
          const lines = await saverulesDashboardService.getReconcilerLines(pro);
          setProLines((m) => ({ ...m, [String(pro)]: lines }));
        } catch {
          setProLines((m) => ({ ...m, [String(pro)]: [] }));
        }
      }
    },
    [expandedPro, proLines],
  );

  const copyCsv = useCallback(() => {
    if (!rows.length) return;
    const cols = Object.keys(rows[0]);
    const csv = [
      cols.join(','),
      ...rows.map((r) =>
        cols
          .map((c) => {
            const v = (r as any)[c];
            const s = v == null ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v);
            return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
          })
          .join(','),
      ),
    ].join('\n');
    navigator.clipboard?.writeText(csv);
  }, [rows]);

  const copyPros = useCallback(() => {
    const pros = Array.from(
      new Set(rows.map((r) => (r as any).pronumber).filter((p) => p != null && p !== '')),
    );
    navigator.clipboard?.writeText(pros.join('\n'));
  }, [rows]);

  // Read-only drill-down: Alt+C closes. Claim tracks `target`, the same thing
  // that gates the render below.
  useAltHotkeys({ C: onClose }, undefined, { layer: !!target });

  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(!!target);

  if (!target) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[70] flex items-start justify-center p-4 sm:p-8">
      <div className="absolute inset-0 bg-scrim/40" onClick={onClose} />
      <div
        ref={panelRef}
        style={panelStyle}
        className="relative z-10 flex max-h-full w-full max-w-6xl flex-col overflow-hidden rounded-lg border border-slate-200 bg-white text-slate-900 shadow-xl dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
      >
        <ModalHeader
          title={
            <span className="flex items-center gap-3">
              {saveDrill && (
                <button
                  onClick={() => setSaveDrill(null)}
                  className="text-sm font-normal text-sky-300 hover:underline"
                >
                  ‹ back
                </button>
              )}
              <span className="truncate">
                {saveDrill ? `All diffs on save · pro ${saveDrill.pronumber}` : target.title}
              </span>
              <span className="text-xs font-normal text-slate-300 tabular-nums">
                {loading ? '…' : `${total.toLocaleString()} rows`}
              </span>
            </span>
          }
          onClose={onClose}
          dragHandleProps={dragHandleProps}
          actions={
            !saveDrill ? (
              <span className="text-slate-300">
                <Sparkline points={trend} />
              </span>
            ) : undefined
          }
        />

        {/* Toolbar */}
        {!saveDrill && (
          <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-2 text-sm dark:border-slate-800">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search pro / user / rule / field / value…"
              className="w-72 rounded-md border border-slate-300 bg-white px-2 py-1 dark:border-slate-700 dark:bg-slate-950"
            />
            {(filter.user || filter.rule || filter.field) && (
              <button
                onClick={() => setFilter({})}
                className="rounded-md bg-slate-100 px-2 py-1 text-xs dark:bg-slate-800"
              >
                clear filter: {filter.user || filter.rule || filter.field} ✕
              </button>
            )}
            <div className="ml-auto flex items-center gap-1">
              {(['rows', 'rule', 'user'] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  className={
                    'rounded-md px-2 py-1 text-xs ' +
                    (tab === t
                      ? 'bg-sky-100 text-sky-700 dark:bg-sky-950/60 dark:text-sky-300'
                      : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800')
                  }
                >
                  {t === 'rows' ? 'Rows' : t === 'rule' ? 'By rule' : 'By user'}
                </button>
              ))}
              <button onClick={copyPros} className="rounded-md px-2 py-1 text-xs text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800">
                Copy pros
              </button>
              <button onClick={copyCsv} className="rounded-md px-2 py-1 text-xs text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800">
                Copy CSV
              </button>
            </div>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-auto p-4">
          {error && <div className="mb-3 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200">{error}</div>}

          {tab !== 'rows' && !saveDrill ? (
            <RollupList rows={rollup} onPick={(key) => {
              setFilter(tab === 'user' ? { user: key } : { rule: key });
              setTab('rows');
            }} />
          ) : recon && !saveDrill ? (
            <ReconcilerTable
              rows={rows as ReconcilerDrillRow[]}
              onOpenPro={onOpenPro}
              expandedPro={expandedPro}
              proLines={proLines}
              onToggleLines={toggleProLines}
              onViewSave={(r) => r.requestId && setSaveDrill({ requestId: r.requestId, pronumber: r.pronumber ?? '' })}
            />
          ) : (
            <RowsTable
              rows={rows as DrilldownRow[]}
              onOpenPro={onOpenPro}
              onViewSave={(r) => r.requestId && setSaveDrill({ requestId: r.requestId, pronumber: r.pronumber ?? '' })}
              hideSaveAction={!!saveDrill}
            />
          )}

          {!loading && rows.length === 0 && tab === 'rows' && (
            <div className="py-10 text-center text-sm text-slate-400">No rows in this window.</div>
          )}
          {total > rows.length && tab === 'rows' && (
            <div className="pt-3 text-center text-xs text-slate-400">
              showing {rows.length.toLocaleString()} of {total.toLocaleString()} — refine with search to narrow
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

// ---- sub-tables -----------------------------------------------------------

const RollupList: React.FC<{ rows: RollupRow[]; onPick: (key: string) => void }> = ({ rows, onPick }) => {
  const max = Math.max(1, ...rows.map((r) => r.n));
  return (
    <div className="flex flex-col gap-1.5">
      {rows.map((r) => (
        <button key={r.key} onClick={() => onPick(r.key)} className="flex items-center gap-3 text-left">
          <span className="w-56 shrink-0 truncate text-xs" title={r.key}>{r.key}</span>
          <span className="h-4 flex-1 overflow-hidden rounded bg-slate-100 dark:bg-slate-800">
            <span className="block h-full bg-sky-500" style={{ width: `${Math.max(2, (100 * r.n) / max)}%` }} />
          </span>
          <span className="w-24 shrink-0 text-right text-xs tabular-nums">{r.n.toLocaleString()} · {r.pros} pros</span>
        </button>
      ))}
      {!rows.length && <div className="py-6 text-center text-sm text-slate-400">No data.</div>}
    </div>
  );
};

const Th: React.FC<{ children?: React.ReactNode; className?: string }> = ({ children, className }) => (
  <th className={`py-1 pr-3 text-left font-medium text-slate-500 dark:text-slate-400 ${className ?? ''}`}>{children}</th>
);

const RowsTable: React.FC<{
  rows: DrilldownRow[];
  onOpenPro: Props['onOpenPro'];
  onViewSave: (r: DrilldownRow) => void;
  hideSaveAction: boolean;
}> = ({ rows, onOpenPro, onViewSave, hideSaveAction }) => (
  <div className="overflow-x-auto">
    <table className="min-w-full text-xs">
      <thead>
        <tr className="border-b border-slate-100 dark:border-slate-800">
          <Th>Pro</Th><Th>Leg</Th><Th>Field</Th><Th>Diff</Th>
          <Th>Python</Th><Th>V1</Th><Th>Rule</Th><Th>User</Th><Th>When</Th><Th></Th>
        </tr>
      </thead>
      <tbody className="align-top">
        {rows.map((r) => (
          <tr key={r.id} className="border-b border-slate-50 hover:bg-slate-50 dark:border-slate-800/60 dark:hover:bg-slate-800/40">
            <td className="py-1 pr-3"><ProLink pro={r.pronumber} onOpenPro={onOpenPro} /></td>
            <td className="py-1 pr-3 tabular-nums">{r.legNum ?? ''}</td>
            <td className="py-1 pr-3">{r.fieldName}</td>
            <td className="py-1 pr-3 whitespace-nowrap">
              <DiffBadge kind={r.diffKind} />
              {r.v1Defect && <V1DefectBadge defect={r.v1Defect} />}
            </td>
            <td className="py-1 pr-3 text-sky-700 dark:text-sky-300 max-w-[16rem] truncate" title={r.pythonValue ?? ''}>{r.pythonValue ?? <span className="text-slate-400">∅</span>}</td>
            <td className="py-1 pr-3 text-amber-700 dark:text-amber-300 max-w-[16rem] truncate" title={r.v1Value ?? ''}>{r.v1Value ?? <span className="text-slate-400">∅</span>}</td>
            <td className="py-1 pr-3 text-slate-500">{r.ruleName ?? '—'}</td>
            <td className="py-1 pr-3">{r.userId}</td>
            <td className="py-1 pr-3 whitespace-nowrap text-slate-500">{fmtTime(r.createdAt)}</td>
            <td className="py-1">
              {!hideSaveAction && r.requestId && (
                <button onClick={() => onViewSave(r)} className="text-sky-600 hover:underline dark:text-sky-400" title="all diffs on this save">
                  save ›
                </button>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const DiffBadge: React.FC<{ kind: string | null }> = ({ kind }) => {
  const missing = kind?.includes('missing');
  const extra = kind?.includes('extra');
  const cls = missing
    ? 'bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-300'
    : extra
    ? 'bg-violet-100 text-violet-700 dark:bg-violet-950/50 dark:text-violet-300'
    : 'bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300';
  return <span className={`rounded px-1.5 py-0.5 ${cls}`}>{kind}</span>;
};

/**
 * A value-diff already traced to a bug on the V1 side — our value is the correct one.
 * Deliberately teal rather than red: this is a finding, not port debt. Hover for the
 * root cause (source of truth: backend known_v1_defects.py).
 */
const V1DefectBadge: React.FC<{ defect: NonNullable<DrilldownRow['v1Defect']> }> = ({ defect }) => (
  <span
    className="ml-1 cursor-help rounded bg-teal-100 px-1.5 py-0.5 text-teal-800 dark:bg-teal-950/50 dark:text-teal-300"
    title={`${defect.label} — ${defect.note}`}
  >
    v1 defect
  </span>
);

const Side: React.FC<{ side: ReconcilerDrillRow['before'] }> = ({ side }) =>
  side ? (
    <span className="tabular-nums">
      {side.qty}·{side.rate}·{side.type}·${side.total}
    </span>
  ) : (
    <span className="text-slate-400">no line</span>
  );

const ReconcilerTable: React.FC<{
  rows: ReconcilerDrillRow[];
  onOpenPro: Props['onOpenPro'];
  expandedPro: string | number | null;
  proLines: Record<string, ReconcilerLine[]>;
  onToggleLines: (pro: string | number) => void;
  onViewSave: (r: ReconcilerDrillRow) => void;
}> = ({ rows, onOpenPro, expandedPro, proLines, onToggleLines, onViewSave }) => (
  <div className="overflow-x-auto">
    <table className="min-w-full text-xs">
      <thead>
        <tr className="border-b border-slate-100 dark:border-slate-800">
          <Th></Th><Th>Pro</Th><Th>Line item</Th><Th>Before (qty·rate·type·$)</Th><Th>After</Th>
          <Th>Type</Th><Th>Reason</Th><Th>User</Th><Th>When</Th><Th></Th>
        </tr>
      </thead>
      <tbody className="align-top">
        {rows.map((r) => (
          <React.Fragment key={r.id}>
            <tr className={`border-b border-slate-50 dark:border-slate-800/60 ${r.isReal ? '' : 'opacity-60'}`}>
              <td className="py-1 pr-2">
                <button onClick={() => r.pronumber != null && onToggleLines(r.pronumber)} title="show current invoice lines">
                  <ChevronRightIcon className={`h-4 w-4 transition-transform ${expandedPro === r.pronumber ? 'rotate-90' : ''}`} />
                </button>
              </td>
              <td className="py-1 pr-3"><ProLink pro={r.pronumber} onOpenPro={onOpenPro} /></td>
              <td className="py-1 pr-3">{r.item}</td>
              <td className="py-1 pr-3"><Side side={r.before} /></td>
              <td className="py-1 pr-3 text-sky-700 dark:text-sky-300"><Side side={r.after} /></td>
              <td className="py-1 pr-3">
                <span className={`rounded px-1.5 py-0.5 ${r.isReal ? 'bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-300' : 'bg-slate-100 text-slate-500 dark:bg-slate-800'}`}>{r.optype}</span>
              </td>
              <td className="py-1 pr-3 text-slate-500 max-w-[14rem] truncate" title={r.reason}>{r.reason}</td>
              <td className="py-1 pr-3">{r.userId}</td>
              <td className="py-1 pr-3 whitespace-nowrap text-slate-500">{fmtTime(r.createdAt)}</td>
              <td className="py-1">
                {r.requestId && <button onClick={() => onViewSave(r)} className="text-sky-600 hover:underline dark:text-sky-400">save ›</button>}
              </td>
            </tr>
            {expandedPro === r.pronumber && (
              <tr className="bg-slate-50 dark:bg-slate-950/40">
                <td />
                <td colSpan={9} className="py-2 pr-3">
                  <LiveLines lines={proLines[String(r.pronumber)]} />
                </td>
              </tr>
            )}
          </React.Fragment>
        ))}
      </tbody>
    </table>
  </div>
);

const LiveLines: React.FC<{ lines?: ReconcilerLine[] }> = ({ lines }) => {
  if (!lines) return <span className="text-xs text-slate-400">loading current invoice lines…</span>;
  if (!lines.length) return <span className="text-xs text-slate-400">no invoice lines (or cms unreachable)</span>;
  return (
    <div className="rounded border border-slate-200 dark:border-slate-700">
      <div className="px-2 py-1 text-[0.6875rem] font-medium text-slate-500">Current invoice lines</div>
      <table className="min-w-full text-[0.6875rem]">
        <thead className="text-slate-400">
          <tr><Th>Acc</Th><Th>#</Th><Th>Item</Th><Th>Region</Th><Th>Qty</Th><Th>Rate</Th><Th>Type</Th><Th>Total</Th><Th>Description</Th></tr>
        </thead>
        <tbody>
          {lines.map((l, i) => (
            <tr key={i} className="border-t border-slate-100 dark:border-slate-800">
              <td className="py-0.5 pr-3 tabular-nums">{String(l.accessorial)}</td>
              <td className="py-0.5 pr-3 tabular-nums">{l.lineNumber}</td>
              <td className="py-0.5 pr-3">{l.item}</td>
              <td className="py-0.5 pr-3">{l.region}</td>
              <td className="py-0.5 pr-3 tabular-nums">{String(l.qty ?? '')}</td>
              <td className="py-0.5 pr-3 tabular-nums">{String(l.rate ?? '')}</td>
              <td className="py-0.5 pr-3">{l.rateType}</td>
              <td className="py-0.5 pr-3 tabular-nums">{String(l.total ?? '')}</td>
              <td className="py-0.5 pr-3 text-slate-500 max-w-[18rem] truncate" title={l.description ?? ''}>{l.description}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default SaverulesDrilldownModal;
